import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Download,
  Edit2,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Shield,
  SlidersHorizontal,
  Upload,
  User,
} from "lucide-react";
import {
  useEmployees,
  useDepartmentOptions,
  useActivateEmployee,
  useDeactivateEmployee,
} from "../hooks";
import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { exportToExcel, exportToPdf } from "#/lib/exportUtils";
import { resolveStatus } from "../types";
import type { EmploymentStatus, EmploymentType, EmployeeRow } from "../types";
import EmployeeImportModal from "../components/EmployeeImportModal";

// ─── look-up maps ─────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Active", ON_PROBATION: "On Probation", ON_LEAVE: "On Leave",
  SUSPENDED: "Suspended", TERMINATED: "Terminated", RESIGNED: "Resigned",
};
const STATUS_COLOR: Record<string, string> = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  ON_PROBATION: "bg-amber-100 text-amber-700",
  ON_LEAVE: "bg-blue-100 text-blue-700",
  SUSPENDED: "bg-red-100 text-red-700",
  TERMINATED: "bg-slate-200 text-slate-500",
  RESIGNED: "bg-purple-100 text-purple-700",
};
const TYPE_LABEL: Record<string, string> = {
  PERMANENT: "Permanent", CONTRACT: "Contract", INTERN: "Intern",
  TEMPORARY: "Temporary", CONSULTANT: "Consultant", CASUAL: "Casual", OTHER: "Other",
};
const TYPE_COLOR: Record<string, string> = {
  PERMANENT: "bg-slate-100 text-slate-600",
  CONTRACT: "bg-orange-50 text-orange-700",
  INTERN: "bg-cyan-100 text-cyan-700",
  TEMPORARY: "bg-yellow-50 text-yellow-700",
  CONSULTANT: "bg-violet-100 text-violet-700",
  CASUAL: "bg-pink-100 text-pink-700",
  OTHER: "bg-slate-100 text-slate-500",
};

const AVATAR_COLORS = [
  "bg-emerald-500", "bg-blue-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-teal-500", "bg-indigo-500", "bg-orange-500",
];
function avatarColor(name: string) {
  let n = 0; for (const c of name) n += c.charCodeAt(0);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");
}
function roleLabel(code: string) {
  return code.replace(/^ROLE_/, "").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}
/** Whole days from today until the given date (negative once it's in the past). */
function daysUntil(v?: string | null): number | null {
  if (!v) return null;
  const target = new Date(`${v}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

function buildPageNums(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const always = new Set([0, 1, current - 1, current, current + 1, total - 2, total - 1]);
  const inRange = [...always].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const pages: (number | "…")[] = [];
  let prev = -1;
  for (const p of inRange) { if (prev !== -1 && p - prev > 1) pages.push("…"); pages.push(p); prev = p; }
  return pages;
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function EmployeesPage() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EmploymentStatus | "ALL">("ALL");
  const [employmentType, setEmploymentType] = useState<EmploymentType | "ALL">("ALL");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showImport, setShowImport] = useState(false);

  const { data, isLoading, isError, error } = useEmployees({ page, size, search, status, employmentType });
  const employees = data?.content ?? [];
  const totalPages = data?.totalPages ?? 1;
  const pageNums = buildPageNums(page, totalPages);

  async function handlePdf() {
    setExporting(true);
    try {
      await exportToPdf({
        title: "Employee Directory",
        subtitle: `${data?.totalElements ?? employees.length} employees · ${new Date().toLocaleString()}`,
        headers: ["Emp No.", "Full Name", "Job Title", "Dept", "Status", "Type", "Email", "Phone"],
        rows: employees.map((e) => [
          e.employeeNumber, e.fullName, e.jobTitle ?? "", e.departmentName ?? "",
          STATUS_LABEL[resolveStatus(e) ?? ""] ?? (resolveStatus(e) ?? ""),
          TYPE_LABEL[e.employmentType ?? ""] ?? (e.employmentType ?? ""),
          e.workEmail ?? "", e.mobilePhone ?? "",
        ]),
        filename: "employees",
      });
    } finally { setExporting(false); }
  }

  async function handleExcel() {
    setExportingExcel(true);
    try {
      await exportToExcel(
        "Employee Directory",
        ["Emp No.", "Full Name", "Job Title", "Dept", "Status", "Type", "Email", "Phone", "System Access", "Hire Date"],
        employees.map((e) => [
          e.employeeNumber, e.fullName, e.jobTitle ?? "", e.departmentName ?? "",
          STATUS_LABEL[e.employmentStatus ?? ""] ?? (e.employmentStatus ?? ""),
          TYPE_LABEL[e.employmentType ?? ""] ?? (e.employmentType ?? ""),
          e.workEmail ?? "", e.mobilePhone ?? "",
          e.systemAccessEnabled ? "Yes" : "No", e.hireDate ?? "",
        ]),
        "employees",
      );
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Employees</h1>
          <p className="text-sm text-slate-500">
            <span className="font-bold text-slate-700">{data?.totalElements ?? 0}</span> total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={handlePdf} disabled={exporting || isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-red-50 disabled:opacity-40">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-500" />}
            PDF
          </button>
          <button type="button" onClick={handleExcel} disabled={exportingExcel || isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-emerald-50 disabled:opacity-40">
            {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-emerald-600" />} Excel
          </button>
          <button type="button" onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-emerald-50">
            <Upload className="h-4 w-4 text-emerald-600" /> Import
          </button>
          <Link to="/employees/new"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800">
            <Plus className="h-4 w-4" /> Add Employee
          </Link>
        </div>
      </div>

      {showImport && (
        <EmployeeImportModal onClose={() => setShowImport(false)} />
      )}

      {/* Stats chips */}
      {!isLoading && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Total", value: data?.totalElements ?? 0, cls: "bg-white border-slate-200 text-slate-700" },
            { label: "Active",   value: employees.filter((e) => resolveStatus(e) === "ACTIVE").length,   cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { label: "On Leave", value: employees.filter((e) => resolveStatus(e) === "ON_LEAVE").length, cls: "bg-blue-50 border-blue-200 text-blue-800" },
            { label: "With Access", value: employees.filter((e) => e.systemAccessEnabled).length, cls: "bg-violet-50 border-violet-200 text-violet-800" },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl border px-3 py-1.5 text-xs font-bold ${s.cls}`}>
              {s.label}: {s.value}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid gap-2 rounded-2xl border bg-white p-3 shadow-sm sm:grid-cols-4">
        <div className="relative sm:col-span-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search name, number, email…"
            className="w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value as EmploymentStatus | "ALL"); setPage(0); }}
          className="rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="ALL">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={employmentType} onChange={(e) => { setEmploymentType(e.target.value as EmploymentType | "ALL"); setPage(0); }}
          className="rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="ALL">All types</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b bg-slate-50">
              <tr className="text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-4 py-2.5 text-left">Job Title</th>
                <th className="px-4 py-2.5 text-left">Department</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Employment Type</th>
                <th className="px-4 py-2.5 text-left">Contact</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td></tr>
              ) : isError ? (
                <tr><td colSpan={7} className="py-10 text-center">
                  <div className="mx-auto max-w-sm rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <p className="font-bold">Failed to load employees</p>
                    <p className="mt-1 font-mono text-xs opacity-80">
                      {(error as any)?.response?.data?.message || (error as any)?.message || String(error)}
                    </p>
                  </div>
                </td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan={7} className="py-12 text-center">
                  <SlidersHorizontal className="mx-auto mb-2 h-7 w-7 text-slate-300" />
                  <p className="text-sm text-slate-500">No employees found. Try adjusting your filters.</p>
                </td></tr>
              ) : employees.map((emp) => (
                <>
                  <EmployeeDataRow
                    key={emp.id}
                    emp={emp}
                    expanded={expandedId === emp.id}
                    onToggleExpand={() => setExpandedId(expandedId === emp.id ? null : emp.id)}
                  />
                  {expandedId === emp.id && (
                    <EmployeeExpandRow key={`expand-${emp.id}`} emp={emp} />
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!isLoading && (data?.totalPages ?? 0) > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span><span className="font-bold text-slate-800">{data?.totalElements ?? 0}</span> employees</span>
            <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
              className="rounded-lg border px-2 py-1 text-xs outline-none focus:border-emerald-500">
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <PgBtn onClick={() => setPage(0)} disabled={page === 0} label="«" />
            <PgBtn onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0} label="‹" />
            {pageNums.map((p, i) =>
              p === "…" ? <span key={`e${i}`} className="px-1 text-xs text-slate-400">…</span> :
                <button key={p} type="button" onClick={() => setPage(p)}
                  className={`flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-bold transition ${p === page ? "border-emerald-600 bg-emerald-600 text-white" : "hover:bg-slate-50"}`}>
                  {p + 1}
                </button>
            )}
            <PgBtn onClick={() => setPage((p) => p + 1)} disabled={data?.last ?? true} label="›" />
            <PgBtn onClick={() => setPage((data?.totalPages ?? 1) - 1)} disabled={data?.last ?? true} label="»" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main data row ────────────────────────────────────────────────────────────

function EmployeeDataRow({
  emp,
  expanded,
  onToggleExpand,
}: {
  emp: EmployeeRow;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const empStatus = resolveStatus(emp);
  const empType   = emp.employmentType ?? null;
  const primaryDept = emp.departmentName ?? emp.departments?.find((d) => d.id === emp.primaryDepartmentId)?.name ?? emp.departments?.[0]?.name;

  return (
    <tr className={`border-t text-sm transition-colors ${expanded ? "bg-emerald-50/40" : "hover:bg-slate-50/60"}`}>

      {/* Employee */}
      <td className="px-4 py-2">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-black text-white ${avatarColor(emp.fullName)}`}>
            {initials(emp.fullName)}
          </div>
          <div className="min-w-0">
            <Link to={`/employees/${emp.id}`} className="block truncate font-bold text-slate-900 hover:text-emerald-700 text-sm">
              {emp.fullName}
            </Link>
            <p className="text-xs text-slate-400">{emp.employeeNumber}</p>
          </div>
        </div>
      </td>

      {/* Job Title */}
      <td className="px-4 py-2">
        <span className="text-sm font-medium text-slate-800">
          {emp.jobTitle || <span className="text-slate-400">—</span>}
        </span>
      </td>

      {/* Department */}
      <td className="px-4 py-2">
        {primaryDept ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">{primaryDept}</span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-2">
        {empStatus ? (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLOR[empStatus] ?? "bg-slate-100 text-slate-500"}`}>
            {STATUS_LABEL[empStatus] ?? empStatus.replaceAll("_", " ")}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>

      {/* Employment Type */}
      <td className="px-4 py-2">
        {empType ? (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${TYPE_COLOR[empType] ?? "bg-slate-100 text-slate-500"}`}>
            {TYPE_LABEL[empType] ?? empType.replaceAll("_", " ")}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>

      {/* Contact */}
      <td className="px-4 py-2">
        <div className="text-xs text-slate-600 truncate max-w-[160px]">{emp.workEmail || emp.personalEmail || "—"}</div>
        {emp.mobilePhone && <div className="text-xs text-slate-400">{emp.mobilePhone}</div>}
      </td>

      {/* Actions */}
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          <Link to={`/employees/${emp.id}`}
            className="rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50">
            View
          </Link>
          <button
            type="button"
            onClick={onToggleExpand}
            className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-bold transition-colors ${
              expanded ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Edit2 className="h-3 w-3" />
            Edit
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Expanded detail row ──────────────────────────────────────────────────────

function EmployeeExpandRow({ emp }: { emp: EmployeeRow }) {
  const roles = (emp.roleCodes ?? emp.user?.roles ?? []).filter((r) => r !== "EMPLOYEE_SELF_SERVICE");

  const { hasRole, hasPermission } = useAuth();
  const confirm = useConfirm();
  const canManage = hasRole("SUPER_ADMIN") || hasPermission("HR_EMPLOYEE_MANAGE");
  const status = resolveStatus(emp);
  const isDisabled = status === "SUSPENDED" || status === "TERMINATED" || !emp.systemAccessEnabled;

  const activateMutation = useActivateEmployee();
  const deactivateMutation = useDeactivateEmployee();
  const togglingAccess = activateMutation.isPending || deactivateMutation.isPending;

  async function handleToggleActive() {
    const name = emp.fullName || `${emp.firstName} ${emp.lastName}`;
    if (isDisabled) {
      if (!(await confirm(`Reactivate ${name}? This restores their system login access.`))) return;
      activateMutation.mutate(emp.id);
    } else {
      if (!(await confirm(`Deactivate ${name}? This blocks their system login immediately.`))) return;
      deactivateMutation.mutate(emp.id);
    }
  }

  const contractDaysLeft = daysUntil(emp.contractEndDate);

  return (
    <tr className="border-t border-emerald-100 bg-emerald-50/30">
      <td colSpan={7} className="px-5 py-3">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">

          {/* Employment details */}
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Employment</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-1.5 text-slate-600">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                Hire date: <span className="font-semibold text-slate-800">{fmtDate(emp.hireDate)}</span>
              </div>
              {emp.contractEndDate && (
                <div
                  className={`flex items-center gap-1.5 ${
                    contractDaysLeft !== null && contractDaysLeft < 0
                      ? "text-red-600 font-semibold"
                      : contractDaysLeft !== null && contractDaysLeft <= 14
                        ? "text-amber-600 font-semibold"
                        : "text-slate-600"
                  }`}
                >
                  <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                  Contract ends: <span className="font-semibold">{fmtDate(emp.contractEndDate)}</span>
                  {contractDaysLeft !== null && contractDaysLeft < 0 && " (expired)"}
                  {contractDaysLeft !== null && contractDaysLeft >= 0 && contractDaysLeft <= 14 && " (expiring soon)"}
                </div>
              )}
              {emp.departments && emp.departments.length > 1 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {emp.departments.map((d) => (
                    <span key={d.id} className="rounded-full bg-white border px-2 py-0.5 text-xs text-slate-600">{d.name}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Contact</p>
            <div className="space-y-1 text-xs text-slate-600">
              {(emp.workEmail || emp.personalEmail) && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{emp.workEmail || emp.personalEmail}</span>
                </div>
              )}
              {emp.personalEmail && emp.workEmail && (
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{emp.personalEmail}</span>
                </div>
              )}
              {emp.mobilePhone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  {emp.mobilePhone}
                </div>
              )}
              {emp.alternativePhone && (
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  {emp.alternativePhone}
                </div>
              )}
              {!emp.workEmail && !emp.personalEmail && !emp.mobilePhone && <span className="text-slate-400">No contact info</span>}
            </div>
          </div>

          {/* System access & roles */}
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">System Access</p>
            <div className="space-y-1">
              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${emp.systemAccessEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <Shield className="mr-1 inline h-3 w-3" />{emp.systemAccessEnabled ? "Enabled" : "Disabled"}
              </span>
              {emp.user?.username && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <User className="h-3.5 w-3.5 shrink-0" />@{emp.user.username}
                </div>
              )}
              {roles.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {roles.map((r) => (
                    <span key={r} className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">{roleLabel(r)}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-col justify-between">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Quick Actions</p>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/employees/${emp.id}/edit`}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-700 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-800"
              >
                <Edit2 className="h-3.5 w-3.5" /> Open Full Edit
              </Link>
              <Link
                to={`/employees/${emp.id}`}
                className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold text-slate-700 hover:bg-white"
              >
                <ExternalLink className="h-3.5 w-3.5" /> View Profile
              </Link>
              {canManage && (
                <button
                  type="button"
                  disabled={togglingAccess}
                  onClick={handleToggleActive}
                  className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold disabled:opacity-60 ${
                    isDisabled
                      ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      : "border-red-300 text-red-700 hover:bg-red-50"
                  }`}
                >
                  <Shield className="h-3.5 w-3.5" />
                  {togglingAccess ? "Working..." : isDisabled ? "Activate" : "Deactivate"}
                </button>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function PgBtn({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-bold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
      {label}
    </button>
  );
}
